import React, { useState } from 'react';
import { reportPdfUrl, exportZipUrl } from '../pages/ai-interview/utils/aiInterviewApi';
import { Icon } from './reportIcons';

/**
 * Download section: the evaluation report as a PDF, and the whole interview (report + transcript +
 * recordings + verification images) as a ZIP. A purged/expired video is noted in EXPIRED.txt inside the
 * zip by the backend, so the export always succeeds. Downloads stream directly from the API.
 */
const DownloadSection = ({ companyId, sessionId, candidateId, interviewId, candidateName }) => {
  const ready = companyId != null && sessionId && candidateId;
  const [busy, setBusy] = useState('');

  // Filename base from the candidate name: "Jane Doe" → "Jane_Doe_Interview".
  const base = `${String(candidateName || 'Candidate').trim().replace(/[^\w.-]+/g, '_').replace(/_+/g, '_') || 'Candidate'}_Interview`;

  const download = async (kind) => {
    if (!ready || busy) return;
    setBusy(kind);
    try {
      const url = kind !== 'zip'
        ? reportPdfUrl(companyId, sessionId, candidateId, interviewId, kind === 'full' ? 'full' : 'summary')
        : exportZipUrl(companyId, sessionId, candidateId, interviewId);
      // The ZIP can be large — stream it as a blob so the browser shows a real download, then release it.
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = kind === 'summary' ? `${base}_Summary.pdf` : kind === 'full' ? `${base}_Full_Report.pdf` : `${base}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Download failed: ${e.message || e}. Please try again.`);
    } finally {
      setBusy('');
    }
  };

  const Card = ({ id, icon, title, body, cta, accent }) => (
    <button type="button" onClick={() => download(id)} disabled={!ready || !!busy}
      className="flex w-full items-start gap-4 rounded-2xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:border-primary-300 hover:shadow disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900">
      <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl text-white ${accent}`}><Icon name={icon} className="h-6 w-6" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-gray-900 dark:text-white">{title}</span>
        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{body}</span>
        <span className="mt-2 inline-block text-xs font-semibold text-primary-600 dark:text-primary-400">
          {busy === id ? 'Preparing…' : cta}
        </span>
      </span>
    </button>
  );

  return (
    <div className="flex flex-col gap-3">
      <Card id="summary" accent="bg-primary-600" icon="pdf"
        title="Interview summary (PDF)"
        body="Decision, score, coverage, key interview evidence and CV checks."
        cta="Download summary" />
      <Card id="full" accent="bg-slate-700" icon="pdf"
        title="Full evidence report (PDF)"
        body="Detailed assessment, transcript and objective evidence appendix."
        cta="Download full report" />
      <Card id="zip" accent="bg-gradient-to-br from-emerald-500 to-teal-600" icon="zip"
        title="Download full interview (ZIP)"
        body="Report PDF, transcript.json, the screen/camera/audio recordings, verification images and the events timeline. Expired videos are noted inside."
        cta="Download ZIP" />
      {!ready && <p className="text-xs text-amber-500">Downloads are available once the interview data is loaded.</p>}
    </div>
  );
};

export default DownloadSection;
